import { zodResolver } from "@hookform/resolvers/zod";
import type { JSX } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { toast } from "@/shared/lib/toast";

import { useBulkUpdateStock } from "../api/use-bulk-update-stock";
import { bulkEditSchema, toBulkPatchBody, type BulkEditFormValues } from "../lib/bulk-edit-schema";

interface BulkEditModalProps {
  rows: { sku: string; mkp: string }[];
  onClose: () => void;
  onApplied: () => void;
}

const FIELD_CLASS =
  "h-10 w-full rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-3 text-sm outline-none transition focus:border-[var(--brand-500)] focus-ring disabled:opacity-50 num";

export function BulkEditModal({ rows, onClose, onApplied }: BulkEditModalProps): JSX.Element {
  const { t } = useTranslation("inventory");
  const { mutate, isPending } = useBulkUpdateStock();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BulkEditFormValues>({ resolver: zodResolver(bulkEditSchema) });

  function onSubmit(values: BulkEditFormValues): void {
    mutate(
      { rows, body: toBulkPatchBody(values) },
      {
        onSuccess: () => {
          toast.success(t("bulk.edit.success", { count: rows.length }));
          onApplied();
          onClose();
        },
      },
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t("bulk.edit.title")}
      data-testid="bulk-edit-modal"
    >
      <button
        type="button"
        aria-label={t("bulk.edit.action_cancel")}
        onClick={onClose}
        className="absolute inset-0 cursor-default"
        style={{ background: "rgba(16,24,40,.28)", backdropFilter: "blur(2px)" }}
      />
      <div
        className="relative w-full max-w-[460px] rounded-2xl bg-white"
        style={{ boxShadow: "var(--sh-modal)" }}
      >
        <h2 className="px-6 pt-5 text-[17px] font-extrabold tracking-tight text-[var(--text-primary)]">
          {t("bulk.edit.title")}
        </h2>
        <p className="px-6 pt-1 text-[12.5px] text-[var(--text-subtle)]">
          {t("bulk.edit.subtitle", { count: rows.length })}
        </p>
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4 px-6 pb-6 pt-4">
          {(["multiplier", "fake", "lock"] as const).map((field) => (
            <div key={field}>
              <label className="mb-1.5 block text-xs font-semibold text-[var(--text-secondary)]">
                {t(`bulk.edit.field_${field}`)}
              </label>
              <input
                type="text"
                inputMode={field === "multiplier" ? "decimal" : "numeric"}
                data-testid={`bulk-edit-${field}`}
                disabled={isPending}
                placeholder={t("bulk.edit.placeholder_unchanged")}
                className={FIELD_CLASS}
                {...register(field, {
                  onChange: (e) => {
                    // Strip anything that isn't a number as the user types — the
                    // backend takes a dot-decimal multiplier and integer fake/lock.
                    const disallowed = field === "multiplier" ? /[^0-9.]/g : /[^0-9]/g;
                    e.target.value = e.target.value.replace(disallowed, "");
                  },
                })}
              />
              {errors[field] && (
                <p
                  className="mt-1 text-xs text-[var(--intent-danger-fg)]"
                  data-testid={
                    field === "multiplier" ? "bulk-edit-error" : `bulk-edit-${field}-error`
                  }
                >
                  {t(errors[field]?.message ?? "")}
                </p>
              )}
            </div>
          ))}
          <div className="flex justify-end gap-2.5 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              data-testid="bulk-edit-cancel"
              className="h-10 rounded-[var(--control-r)] border border-[var(--premium-border-strong)] px-4 text-[13.5px] font-semibold text-[var(--text-secondary)] transition hover:bg-[var(--canvas)] disabled:opacity-50"
            >
              {t("bulk.edit.action_cancel")}
            </button>
            <button
              type="submit"
              disabled={isPending}
              data-testid="bulk-edit-submit"
              className="inline-flex h-10 items-center gap-1.5 rounded-[var(--control-r)] px-5 text-[13.5px] font-bold text-white transition hover:-translate-y-px disabled:opacity-50"
              style={{
                background: "var(--grad-primary-btn)",
                boxShadow:
                  "0 1px 2px rgba(15,98,254,.5),0 6px 16px -4px rgba(15,98,254,.5),inset 0 1px 0 rgba(255,255,255,.22)",
              }}
            >
              {isPending ? t("bulk.edit.action_applying") : t("bulk.edit.action_apply")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
